import { inject } from '@angular/core';
import { HX_DATE_LOCALE } from '../date-picker-tokens';
import { DateAdapter } from './abstract-date-adapter';

/**
 * DateAdapter implementation using JavaScript's native Date object. All operations use the local time zone.
 *
 * This is the default adapter if no other implementation is provided.
 *
 * @example
 *   ```typescript
 *   providers: [
 *     { provide: HX_DATE_ADAPTER, useClass: NativeDateAdapter }
 *   ]
 *   ```;
 */
export class NativeDateAdapter extends DateAdapter<Date> {
  constructor() {
    super();
    const initialLocale = inject(HX_DATE_LOCALE, { optional: true });
    if (initialLocale) {
      this.setLocale(initialLocale);
    }
  }

  override setLocale(locale: string): void {
    this.locale = locale;
  }

  create(year: number, month: number, date: number): Date {
    // Month is 0-indexed in JavaScript Date
    return new Date(year, month, date);
  }

  today(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  clone(date: Date): Date {
    return new Date(date.getTime());
  }

  parse(value: string, parseFormats: string[]): Date | null {
    if (!value || value.trim() === '') {
      return null;
    }

    // Try ISO 8601 format first (YYYY-MM-DD)
    const isoMatch = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (isoMatch) {
      const year = parseInt(isoMatch[1]!, 10);
      const month = parseInt(isoMatch[2]!, 10) - 1; // Convert to 0-indexed
      const day = parseInt(isoMatch[3]!, 10);
      const date = new Date(year, month, day);
      if (this.isValid(date)) {
        return date;
      }
    }

    // Try locale-specific formats (simplified parsing for common patterns)
    for (const format of parseFormats) {
      const parsed = this.parseWithFormat(value, format);
      if (parsed && this.isValid(parsed)) {
        return parsed;
      }
    }

    // Fallback to Date constructor
    const timestamp = Date.parse(value);
    if (!isNaN(timestamp)) {
      return new Date(timestamp);
    }

    return null;
  }

  private parseWithFormat(value: string, format: string): Date | null {
    const trimmed = value.trim();

    // Determine format type by checking the format pattern
    const isDayFirst = format.startsWith('DD') || format.startsWith('D ');
    const isMonthYear = format === 'MM/YYYY';
    const isMonthDay = format === 'MM/DD';
    const isLongMonth = format.includes('MMMM');

    // Handle "DD MMMM YYYY" format (e.g., "15 January 2024")
    if (isLongMonth) {
      const longMonthMatch = trimmed.match(/^(\d{1,2})\s+([a-zA-Z]+)\s+(\d{4})$/);
      if (longMonthMatch) {
        const day = parseInt(longMonthMatch[1]!, 10);
        const monthName = longMonthMatch[2]!.toLowerCase();
        const year = parseInt(longMonthMatch[3]!, 10);

        // Get month index from month name
        const longMonthNames = this.getMonthNames('long').map((m) => m.toLowerCase());
        const shortMonthNames = this.getMonthNames('short').map((m) => m.toLowerCase());
        let monthIndex = longMonthNames.indexOf(monthName);
        if (monthIndex === -1) {
          monthIndex = shortMonthNames.indexOf(monthName);
        }

        if (monthIndex !== -1) {
          return new Date(year, monthIndex, day);
        }
      }
      return null;
    }

    // Handle MM/YYYY format (e.g., "01/2024")
    if (isMonthYear) {
      const monthYearMatch = trimmed.match(/^(\d{1,2})\/(\d{4})$/);
      if (monthYearMatch) {
        const month = parseInt(monthYearMatch[1]!, 10) - 1;
        const year = parseInt(monthYearMatch[2]!, 10);
        return new Date(year, month, 1); // Default to first day of month
      }
      return null;
    }

    // Handle MM/DD format (e.g., "01/15")
    if (isMonthDay) {
      const monthDayMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})$/);
      if (monthDayMatch) {
        const month = parseInt(monthDayMatch[1]!, 10) - 1;
        const day = parseInt(monthDayMatch[2]!, 10);
        const currentYear = new Date().getFullYear();
        return new Date(currentYear, month, day);
      }
      return null;
    }

    // Try slash-separated date patterns (MM/DD/YYYY or DD/MM/YYYY)
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (slashMatch) {
      const first = parseInt(slashMatch[1]!, 10);
      const second = parseInt(slashMatch[2]!, 10);
      let year = parseInt(slashMatch[3]!, 10);

      // Handle 2-digit years
      if (year < 100) {
        year += year < 50 ? 2000 : 1900;
      }

      if (isDayFirst) {
        // DD/MM/YYYY format
        const day = first;
        const month = second - 1; // 0-indexed
        return new Date(year, month, day);
      } else {
        // MM/DD/YYYY format (default)
        const month = first - 1; // 0-indexed
        const day = second;
        return new Date(year, month, day);
      }
    }

    return null;
  }

  format(date: Date, displayFormat: string): string {
    if (!this.isValid(date)) {
      return '';
    }

    // Use pattern-based formatting for consistent results
    return this.formatWithPattern(date, displayFormat);
  }

  /**
   * Formats a date using a specific pattern string. Supported tokens:
   *
   * - YYYY: 4-digit year (2024)
   * - YY: 2-digit year (24)
   * - MM: 2-digit month (01-12)
   * - M: 1 or 2-digit month (1-12)
   * - DD: 2-digit day (01-31)
   * - D: 1 or 2-digit day (1-31)
   * - MMM: Short month name (Jan, Feb)
   * - MMMM: Full month name (January, February)
   */
  private formatWithPattern(date: Date, pattern: string): string {
    const year = this.getYear(date);
    const month = this.getMonth(date) + 1; // 1-indexed
    const day = this.getDate(date);

    // Get month names for locale-aware formatting
    const shortMonthNames = this.getMonthNames('short');
    const longMonthNames = this.getMonthNames('long');

    // Replace tokens in order of specificity (longer tokens first)
    let result = pattern
      .replace(/YYYY/g, year.toString())
      .replace(/YY/g, (year % 100).toString().padStart(2, '0'))
      .replace(/MMMM/g, longMonthNames[month - 1] || '')
      .replace(/MMM/g, shortMonthNames[month - 1] || '')
      .replace(/MM/g, month.toString().padStart(2, '0'))
      .replace(/M(?![a-zA-Z])/g, month.toString())
      .replace(/DD/g, day.toString().padStart(2, '0'))
      .replace(/D(?![a-zA-Z])/g, day.toString());

    return result;
  }

  getYear(date: Date): number {
    return date.getFullYear();
  }

  getMonth(date: Date): number {
    return date.getMonth();
  }

  getDate(date: Date): number {
    return date.getDate();
  }

  getDayOfWeek(date: Date): number {
    return date.getDay();
  }

  addCalendarYears(date: Date, years: number): Date {
    return this.create(this.getYear(date) + years, this.getMonth(date), this.getDate(date));
  }

  addCalendarMonths(date: Date, months: number): Date {
    const newDate = this.clone(date);
    newDate.setMonth(newDate.getMonth() + months);
    return newDate;
  }

  addCalendarDays(date: Date, days: number): Date {
    const newDate = this.clone(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  }

  compareDate(first: Date, second: Date): number {
    const firstTime = this.create(this.getYear(first), this.getMonth(first), this.getDate(first)).getTime();
    const secondTime = this.create(this.getYear(second), this.getMonth(second), this.getDate(second)).getTime();
    return firstTime - secondTime;
  }

  sameDate(first: Date | null, second: Date | null): boolean {
    if (first === null && second === null) {
      return true;
    }
    if (first === null || second === null) {
      return false;
    }
    return this.compareDate(first, second) === 0;
  }

  isValid(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  getFirstDayOfWeek(): number {
    // Get first day of week for locale (0 = Sunday, 1 = Monday, etc.)
    // Most locales use Sunday (0) or Monday (1)
    // This is a simplified implementation; full implementation would use Intl
    return this.locale.startsWith('en') ? 0 : 1;
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const names: string[] = [];
    const format = new Intl.DateTimeFormat(this.locale, { month: style });

    for (let month = 0; month < 12; month++) {
      const date = new Date(2024, month, 1);
      names.push(format.format(date));
    }

    return names;
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const names: string[] = [];
    const format = new Intl.DateTimeFormat(this.locale, { weekday: style });

    // Start from the first day of the week for this locale
    const firstDay = this.getFirstDayOfWeek();
    const baseDate = new Date(2024, 0, 7); // A Sunday

    for (let i = 0; i < 7; i++) {
      const dayOffset = (firstDay + i) % 7;
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + dayOffset);
      names.push(format.format(date));
    }

    return names;
  }

  getNumDaysInMonth(date: Date): number {
    const year = this.getYear(date);
    const month = this.getMonth(date);
    return new Date(year, month + 1, 0).getDate();
  }

  getFirstDateOfMonth(date: Date): Date {
    return this.create(this.getYear(date), this.getMonth(date), 1);
  }

  getLastDateOfMonth(date: Date): Date {
    const year = this.getYear(date);
    const month = this.getMonth(date);
    const numDays = this.getNumDaysInMonth(date);
    return this.create(year, month, numDays);
  }

  toIso8601(date: Date): string {
    const year = this.getYear(date);
    const month = (this.getMonth(date) + 1).toString().padStart(2, '0');
    const day = this.getDate(date).toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  fromIso8601(iso: string): Date | null {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return null;
    }

    const year = parseInt(match[1]!, 10);
    const month = parseInt(match[2]!, 10) - 1;
    const day = parseInt(match[3]!, 10);
    const date = this.create(year, month, day);

    return this.isValid(date) ? date : null;
  }
}
